import { createSlice } from '@reduxjs/toolkit';

import {
    openCompleteAccountSignupModalFromChecker,
    setTrigger,
    stopTracker,
} from '@/redux/modules/account/shouldLogin/shouldLogin.actions';
import { defaultShouldLoginSlice, ShouldLoginSlice } from '@/redux/modules/account/shouldLogin/shouldLogin.types';

import {
    DISMISS_MODAL,
    OPEN_COMPLETE_ACCOUNT_SIGNUP_MODAL,
    type OpenCompleteAccountSignupModalAction,
} from '../../actions';

type SliceModalKey = keyof Omit<ShouldLoginSlice, 'trigger'>;

const openCompleteAccountModal = (slice: ShouldLoginSlice, modal: SliceModalKey, trigger: string): ShouldLoginSlice => {
    return {
        ...slice,
        completeAccountModalOpen: true,
        startTracking: true,
        [`${modal}`]: true,
        trigger,
    };
};

const closeAllModals = (slice: ShouldLoginSlice, clearTracking: boolean, clearTrigger: boolean): ShouldLoginSlice => ({
    ...defaultShouldLoginSlice,
    startTracking: clearTracking ? defaultShouldLoginSlice.startTracking : slice.startTracking,
    trigger: clearTrigger ? defaultShouldLoginSlice.trigger : slice.trigger,
});

const shouldLoginSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(openCompleteAccountSignupModalFromChecker.fulfilled, (slice, { payload: trigger }) =>
            openCompleteAccountModal(slice, 'completeAccountModalOpen', trigger)
        );
        builder.addCase(stopTracker.fulfilled, (slice) => closeAllModals(slice, true, true));
        builder.addCase(DISMISS_MODAL, (slice) => closeAllModals(slice, false, false));

        // openCompleteAccountSignupModalFromChecker dispatches this same action type WITH a trigger,
        // which openCompleteAccountSignupModalFromChecker.fulfilled (above) already tracks. A generic
        // login/signup modal open (e.g. header "Log In", exit-intent, join CTA) dispatches it directly
        // with no trigger, so any stale trigger left over from a dismissed Save/Follow/Register click
        // must not resume when this unrelated modal is opened.
        builder.addCase<typeof OPEN_COMPLETE_ACCOUNT_SIGNUP_MODAL, OpenCompleteAccountSignupModalAction>(
            OPEN_COMPLETE_ACCOUNT_SIGNUP_MODAL,
            (slice, action) => {
                if (!action.trigger) {
                    return closeAllModals(slice, true, true);
                }
                // trigger present — openCompleteAccountSignupModalFromChecker.fulfilled (above) already
                // tracked it, so returning undefined here leaves the slice as Immer's "no change" signal.
            }
        );

        builder.addCase(setTrigger.fulfilled, (state, { payload: trigger }) => {
            state.trigger = trigger;
        });
    },
    initialState: defaultShouldLoginSlice,
    name: 'shouldLogin',
    reducers: {},
});

export const { reducer: shouldLoginReducer } = shouldLoginSlice;
